import { clsx } from "clsx";
import type { ThemeOption } from "@/types/settings";

interface ThemeCardProps {
  theme: ThemeOption;
  label: string;
  description: string;
  isSelected: boolean;
  onSelect: () => void;
}

function ThemePreview({ theme }: { theme: ThemeOption }) {
  if (theme === "dark") {
    return (
      <svg viewBox="0 0 120 80" className="h-full w-full" aria-hidden="true">
        <rect width="120" height="80" fill="#1e293b" rx="4" />
        <rect x="0" y="0" width="30" height="80" fill="#0f172a" rx="4" />
        <rect x="6" y="10" width="18" height="3" fill="#475569" rx="1" />
        <rect x="6" y="18" width="14" height="2" fill="#334155" rx="1" />
        <rect x="6" y="24" width="16" height="2" fill="#334155" rx="1" />
        <rect x="6" y="30" width="12" height="2" fill="#334155" rx="1" />
        <rect x="38" y="10" width="40" height="4" fill="#e2e8f0" rx="1" />
        <rect x="38" y="20" width="74" height="24" fill="#334155" rx="3" />
        <rect x="38" y="50" width="36" height="20" fill="#334155" rx="3" />
        <rect x="78" y="50" width="34" height="20" fill="#334155" rx="3" />
      </svg>
    );
  }

  if (theme === "system") {
    return (
      <svg viewBox="0 0 120 80" className="h-full w-full" aria-hidden="true">
        {/* Light half */}
        <clipPath id="left-half">
          <rect x="0" y="0" width="60" height="80" />
        </clipPath>
        <clipPath id="right-half">
          <rect x="60" y="0" width="60" height="80" />
        </clipPath>
        <g clipPath="url(#left-half)">
          <rect width="120" height="80" fill="#f8fafc" rx="4" />
          <rect x="0" y="0" width="30" height="80" fill="#193B69" rx="4" />
          <rect x="6" y="10" width="18" height="3" fill="#ffffff" rx="1" />
          <rect x="6" y="18" width="14" height="2" fill="#8daacf" rx="1" />
          <rect x="38" y="10" width="40" height="4" fill="#1e293b" rx="1" />
          <rect x="38" y="20" width="74" height="24" fill="#e2e8f0" rx="3" />
          <rect x="38" y="50" width="36" height="20" fill="#e2e8f0" rx="3" />
        </g>
        <g clipPath="url(#right-half)">
          <rect width="120" height="80" fill="#1e293b" rx="4" />
          <rect x="0" y="0" width="30" height="80" fill="#0f172a" rx="4" />
          <rect x="6" y="10" width="18" height="3" fill="#475569" rx="1" />
          <rect x="6" y="18" width="14" height="2" fill="#334155" rx="1" />
          <rect x="38" y="10" width="40" height="4" fill="#e2e8f0" rx="1" />
          <rect x="38" y="20" width="74" height="24" fill="#334155" rx="3" />
          <rect x="78" y="50" width="34" height="20" fill="#334155" rx="3" />
        </g>
        <line
          x1="60"
          y1="0"
          x2="60"
          y2="80"
          stroke="#94a3b8"
          strokeWidth="0.5"
        />
      </svg>
    );
  }

  // Light theme
  return (
    <svg viewBox="0 0 120 80" className="h-full w-full" aria-hidden="true">
      <rect width="120" height="80" fill="#f8fafc" rx="4" />
      <rect x="0" y="0" width="30" height="80" fill="#193B69" rx="4" />
      <rect x="6" y="10" width="18" height="3" fill="#ffffff" rx="1" />
      <rect x="6" y="18" width="14" height="2" fill="#8daacf" rx="1" />
      <rect x="6" y="24" width="16" height="2" fill="#8daacf" rx="1" />
      <rect x="6" y="30" width="12" height="2" fill="#8daacf" rx="1" />
      <rect x="38" y="10" width="40" height="4" fill="#1e293b" rx="1" />
      <rect x="38" y="20" width="74" height="24" fill="#e2e8f0" rx="3" />
      <rect x="38" y="50" width="36" height="20" fill="#e2e8f0" rx="3" />
      <rect x="78" y="50" width="34" height="20" fill="#e2e8f0" rx="3" />
    </svg>
  );
}

export function ThemeCard({
  theme,
  label,
  description,
  isSelected,
  onSelect,
}: ThemeCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={clsx(
        "cursor-pointer rounded-xl border-2 p-3 text-left transition-all",
        isSelected
          ? "border-adara-700 ring-1 ring-adara-200"
          : "border-edge hover:border-edge-hard",
      )}
    >
      {/* Preview */}
      <div className="overflow-hidden rounded-lg border border-edge-soft">
        <ThemePreview theme={theme} />
      </div>

      {/* Info */}
      <div className="mt-3 flex items-center gap-2">
        <div
          className={clsx(
            "flex h-4 w-4 items-center justify-center rounded-full border-2",
            isSelected ? "border-adara-700" : "border-edge-hard",
          )}
        >
          {isSelected && <div className="h-2 w-2 rounded-full bg-adara-700" />}
        </div>
        <div>
          <span className="text-sm font-medium text-main">{label}</span>
          <p className="text-xs text-dim">{description}</p>
        </div>
      </div>
    </button>
  );
}
